import { Breakpoint, Typography } from '@altibox/design-system-component-lib';
import React, { FC } from 'react';
import { useNavigate } from 'react-router-dom';

import { PurchaseEligibilityStatus } from 'src/api/@generated/backend';

import { CustomerDebtAlert } from 'src/components/customer-debt-alert';
import { CustomizedPortableText } from 'src/components/customized-portable-text';
import { DataLayerViewItemWrapper } from 'src/components/data-layer-wrapper/data-layer-view-item-wrapper';
import { i18n } from 'src/i18n';
import { NavigationService } from 'src/services/navigation-service';
import { selectSelectedProductOrThrow } from 'src/state/order/selectors';
import { selectPurchaseEligibilityStatus } from 'src/state/request/selectors';
import { useAppSelector } from 'src/state/thunk-api';
import { formatCurrency } from 'src/utils/currency-utils';
import { I18nUtils } from 'src/utils/i18n-utils';
import { formatMonthlyPriceLong } from 'src/utils/monthly-price-utils';
import { Videos } from 'src/components/videos';

import {
  StyledContainer,
  StyledFootNote,
  StyledImageSlider,
  StyledMonthlyCost,
  StyledNavigationButton,
  StyledTitle,
  StyledTotalPrice,
} from './product-details.styled';

export const ProductDetails: FC = () => {
  const navigate = useNavigate();
  const purchaseEligibilityStatus = useAppSelector(selectPurchaseEligibilityStatus);
  const isInDebt = purchaseEligibilityStatus === PurchaseEligibilityStatus.DUNNING;

  const selectedProduct = useAppSelector(selectSelectedProductOrThrow);
  const { images, name, details, videos, pricing } = selectedProduct;

  const handleButtonClick = () => navigate(NavigationService.SUMMARY);

  return (
    <DataLayerViewItemWrapper product={selectedProduct}>
      <StyledContainer>
        <StyledImageSlider images={images.map((image) => ({ src: image.url, alt: image.altText }))} />
        <StyledTitle variant="headline2" component="h1">
          {name}
        </StyledTitle>
        <CustomizedPortableText
          value={details}
          variablesMap={{ ...pricing }}
          typographyRules={{
            normal: { variant: 'paragraph1' },
          }}
        />
        <Videos videos={videos} />
        <StyledMonthlyCost variant="headline4" component="p" maxBreakpoint={Breakpoint.TABLET}>
          {formatMonthlyPriceLong(pricing.monthlyPrice, pricing.numberOfMonthlyInstallments)}
        </StyledMonthlyCost>
        {pricing.numberOfMonthlyInstallments && pricing.totalPriceForInstallmentPeriod ? (
          <>
            <Typography variant="paragraph3" component="p" maxBreakpoint={Breakpoint.TABLET}>
              {I18nUtils.message(i18n.page.productDetails.totalPriceWithMonths, {
                numberOfMonthlyInstallments: pricing.numberOfMonthlyInstallments,
              })}
              <StyledTotalPrice variant="paragraph3" component="span" maxBreakpoint={Breakpoint.TABLET}>
                {formatCurrency(pricing.totalPriceForInstallmentPeriod)}
              </StyledTotalPrice>
            </Typography>
          </>
        ) : (
          <>
            <Typography variant="paragraph3" component="p" maxBreakpoint={Breakpoint.TABLET}>
              {i18n.page.productDetails.totalPriceWithoutMonths}
              <StyledTotalPrice variant="paragraph3" component="span" maxBreakpoint={Breakpoint.TABLET}>
                {formatCurrency(pricing.firstYearPrice)}
              </StyledTotalPrice>
            </Typography>
          </>
        )}
        <StyledNavigationButton disabled={isInDebt} onClick={handleButtonClick}>
          {i18n.page.productDetails.buttonText.goToSummary}
        </StyledNavigationButton>
        {isInDebt ? (
          <CustomerDebtAlert />
        ) : (
          <>
            {pricing.numberOfMonthlyInstallments && (
              <StyledFootNote variant="paragraph2" component="p" maxBreakpoint={Breakpoint.MOBILE} italic={true}>
                {I18nUtils.message(i18n.page.productDetails.cancellationFootnote, {
                  numberOfMonthlyInstallments: pricing.numberOfMonthlyInstallments,
                })}
              </StyledFootNote>
            )}
          </>
        )}
      </StyledContainer>
    </DataLayerViewItemWrapper>
  );
};
